import { useT } from '@/i18n/runtime';
import { useSessionStore } from '@/store/sessionStore';
import type { CheckKey } from '@/types';
import { checkKeyOfStep, isCheckStepId } from '@/types';
import { CHECK_LABEL_KEYS } from '../stepLabels';
import type { StepProps } from '../types';
import { AppBar } from '../components/AppBar';
import { Button } from '../components/Button';
import { useCheckStepFooter } from './useCheckStepFooter';

/**
 * The honest placeholder for a plan check that has NO dedicated droom component
 * yet (KYCID-FSD-00013 §8). Never a white screen: the customer is told the check
 * is handled with the agent on the call, and Continue advances via the honest
 * fallback (so a REQUIRED check is cleared by the live-OVD path, never dead-ended).
 * Labels come from the shared stepLabels vocabulary (item 7 — one map, reused
 * by the Stepper).
 */

export function MissingCheckStep({ tenant }: StepProps): JSX.Element {
  const currentStep = useSessionStore((s) => s.session.currentStep);
  // We only ever mount for a check step, but stay defensive about the key.
  const key: CheckKey = isCheckStepId(currentStep)
    ? checkKeyOfStep(currentStep)
    : 'DOC_CAPTURE';
  const { t } = useT();
  const labelKey = CHECK_LABEL_KEYS[key];
  const label = labelKey ? t(labelKey) : key;
  const footer = useCheckStepFooter(key);

  return (
    <div className="app-view">
      <AppBar tenant={tenant} subtitle={label} />
      <main className="center-col" style={{ padding: 16 }} aria-label={t('droom.missing.aria', { label })}>
        <section className="card stack" style={{ padding: 16 }}>
          <h2>{label}</h2>
          <p className="sub">{t('droom.missing.body')}</p>
          <Button onClick={() => footer.fallback()}>{t('droom.action.continue')}</Button>
          {footer.isOptional ? (
            <Button variant="ghost" onClick={() => footer.skip()}>
              {t('droom.action.skipStep')}
            </Button>
          ) : null}
        </section>
      </main>
    </div>
  );
}
